/**
 * Note wizard — create (/notes/new) and edit (/notes/:id/edit, Draft or Rejected notes), split
 * exactly like the contract wizard:
 *
 *   1. Note Details       — reference prefix + start + maturity, currency, issuer/programme,
 *                           description, and the investors (amount each; an investor may override
 *                           the start date). A new note whose reference already belongs to a Draft
 *                           note ADDS its investors to that note; to a submitted note, it becomes a
 *                           new note with a letter suffix (…B).
 *   2. Interest & Payments — gross rate, calculation profile (same list as contracts), interest
 *                           payment frequency, capitalization frequency for SPC profiles.
 *   3. Adhoc Events        — not yet available for notes (shown so the split matches contracts).
 *   4. Commission & Tax    — each investor's commission spread, EDITABLE (pre-filled with their
 *                           default: investor override → staff → standard), with the resulting
 *                           net rate and commission amount; WHT as resolved.
 *   5. Review & Schedule   — per-investor figures, total-note and per-investor schedules (contract
 *                           amortization engine, the chosen profile); Save as Draft or Submit the
 *                           whole note for approval.
 *
 * Nothing is written until the last step, and the save is one transaction on the server, so the
 * wizard never leaves a half-saved note.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, TextField, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Checkbox, FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Description as DetailsIcon, Percent as InterestIcon, AccountBalance as AdhocIcon,
  Security as CommissionIcon, CheckCircle as ReviewIcon,
  ArrowBack as BackIcon, ArrowForward as NextIcon, Save as SaveIcon, Send as SubmitIcon,
  Add as AddIcon, Delete as DeleteIcon, Replay as ResetIcon,
} from '@mui/icons-material';
import { apiGet, apiPost, apiPut, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber, formatShortDate } from '../../utils/formatters';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { MODULES } from '../../contexts/PermissionConstants';
import { useNotification } from '../../contexts/NotificationContext';
import WizardStepper from '../../components/contracts/wizard/WizardStepper';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import NoteScheduleTable from '../../components/notes/NoteScheduleTable';
import { SPREAD_SOURCES } from '../../components/notes/NoteLegsTable';
import StakeholderAutocomplete from '../../components/common/StakeholderAutocomplete';
import SubmitForApprovalDialog from '../../components/contracts/SubmitForApprovalDialog';
import {
  NoteDetailsFields, NoteInterestFields, ProfileSummary, useNoteRefData, emptyNoteTerms, defaultProfileId,
  noteDetailsValid, noteInterestValid, noteTermsPayload, effectiveUserReference, prefixOfUserReference,
} from '../../components/notes/NoteDialogs';

// Same five-step split, icons and order as the contract wizard (WizardStepper BASE_STEPS).
const STEPS = [
  { label: 'Note Details', icon: <DetailsIcon /> },
  { label: 'Interest & Payments', icon: <InterestIcon /> },
  { label: 'Adhoc Events', icon: <AdhocIcon /> },
  { label: 'Commission & Tax', icon: <CommissionIcon /> },
  { label: 'Review & Schedule', icon: <ReviewIcon /> },
];
const LAST = STEPS.length - 1;
const COMMISSION_STEP = 3;

let keySeq = 0;
const newKey = () => { keySeq += 1; return `k${keySeq}`; };
const emptyLeg = () => ({
  key: newKey(), leg_id: null, investor_id: null, nominal_amount: '', own_date: false, issue_date: '', commission_spread: null,
});
// commission_spread: null = use the investor's default spread; a string = the text typed for
// this investor (kept verbatim while editing, so clearing the field does not snap it back).
const isSpreadEdited = (leg) => Boolean(leg) && leg.commission_spread !== null;
const SPREAD_TEXT = /^\d*\.?\d*$/;
const legFromNote = (l) => ({
  key: newKey(),
  leg_id: l.id,
  investor_id: l.investor_id,
  nominal_amount: l.nominal_amount,
  own_date: Boolean(l.issue_date_override),
  issue_date: l.issue_date_override || '',
  commission_spread: l.spread_override === null || l.spread_override === undefined ? null : String(l.spread_override),
});
const money = (v) => (v === null || v === undefined ? '-' : formatNumber(v, 2));
const pct = (v) => (v === null || v === undefined || v === '' ? '-' : `${Number(v).toFixed(2)}%`);
const WHT_SOURCES = { company_exempt: 'Company — exempt', investor_exempt: 'Investor exempt' };

/** Terms of an existing Draft note, in the wizard's form shape (new investors join on these). */
const termsFromNote = (note, prefix) => ({
  ...emptyNoteTerms(null, note),
  reference_prefix: prefix,
  capitalization_frequency_id: note.capitalization_frequency_id || '',
  issuer_id: note.issuer_id || '',
  program_id: note.program_id || '',
  description: note.description || '',
});

const NoteCreate = () => {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const notify = useNotification();
  const { hasAction } = usePermissions();
  const { isModuleEnabled } = useTenant();
  const enabled = isModuleEnabled(MODULES.NOTES);
  const canSubmit = hasAction(MODULES.NOTES, 'update');

  const [activeStep, setActiveStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [terms, setTerms] = useState(emptyNoteTerms(null));
  const [editNote, setEditNote] = useState(null);
  const [existing, setExisting] = useState(null);   // Draft note this new note's investors will join
  const [lookup, setLookup] = useState(null);       // { reference, suffixed }
  const [legs, setLegs] = useState([emptyLeg()]);
  const [investors, setInvestors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // T3-161: "Submit" follows the contract wizard — save the note as a draft first, then open the
  // submit-for-approval dialog on it (required documents per the investment type, uploaded there).
  const [submitNoteId, setSubmitNoteId] = useState(null);
  const onRefError = useCallback((msg) => setError(msg), []);
  const refData = useNoteRefData(enabled, terms.issuer_id, onRefError, terms.investment_type_id);

  useEffect(() => {
    if (!enabled) return;
    Promise.all([
      apiGet('/notes/settings'),
      apiGet('/stakeholders/investors/select'),
      isEdit ? apiGet(`/notes/${editId}`) : Promise.resolve(null),
    ]).then(([s, inv, n]) => {
      const st = extractApiData(s, null);
      setSettings(st);
      setInvestors(extractApiData(inv));
      if (n) {
        const note = extractApiData(n, null);
        if (!note.is_editable) {
          navigate(`/notes/${note.id}`, { replace: true });
          return;
        }
        setEditNote(note);
        // T3-158: the prefix comes from the USER reference; a legacy code that breaks the 3-letter
        // rule falls back to the issuer's prefix / the tenant default (the reference is rebuilt on save).
        setTerms(termsFromNote(note, prefixOfUserReference(note.user_reference)
          || note.issuer_note_reference_prefix || st?.referencePrefix || ''));
        setLegs(note.legs.length ? note.legs.map(legFromNote) : [emptyLeg()]);
      } else {
        setTerms((t) => (t.reference_prefix ? t : { ...t, reference_prefix: st?.referencePrefix || '' }));
      }
    }).catch((err) => setError(extractApiError(err, 'Could not load the note')))
      .finally(() => setLoading(false));
  }, [enabled, isEdit, editId, navigate]);

  // Default the calculation profile to the seeded Simple Actual/365 once profiles load.
  useEffect(() => {
    if (!terms.calculation_profile_id && refData.profiles.length) {
      setTerms((t) => ({ ...t, calculation_profile_id: defaultProfileId(refData.profiles) }));
    }
  }, [refData.profiles, terms.calculation_profile_id]);

  if (!enabled) {
    return <Alert severity="info">The Notes module is not enabled for this company.</Alert>;
  }
  if (loading) return <LoadingSpinner />;

  const effectiveTerms = existing ? termsFromNote(existing, terms.reference_prefix) : terms;

  // draft: the existing Draft note these investors would join (its investors count too).
  const legProblems = (t, draft = null) => {
    const problems = [];
    if (legs.length === 0) problems.push('Add at least one investor');
    // An investor appears only ONCE on a note (user, 2026-10-06; T3-159 backs it).
    const nameOf = (id) => investors.find((i) => i.id === id)?.name || 'An investor';
    const seen = new Map();
    legs.forEach((l, i) => {
      if (!l.investor_id) return;
      if (seen.has(l.investor_id)) problems.push(`Investors ${seen.get(l.investor_id)} and ${i + 1} are the same investor (${nameOf(l.investor_id)})`);
      else seen.set(l.investor_id, i + 1);
    });
    (draft?.legs || []).forEach((dl) => {
      if (seen.has(dl.investor_id)) {
        problems.push(`${dl.investor_name || nameOf(dl.investor_id)} is already on draft note ${draft.user_reference} — change their amount there`);
      }
    });
    legs.forEach((l, i) => {
      if (!l.investor_id) problems.push(`Investor ${i + 1}: choose an investor`);
      if (!(Number(l.nominal_amount) > 0)) problems.push(`Investor ${i + 1}: amount must be greater than zero`);
      if (l.own_date && !l.issue_date) problems.push(`Investor ${i + 1}: enter their start date or untick "Own start date"`);
      if (l.own_date && l.issue_date && l.issue_date >= t.maturity_date) {
        problems.push(`Investor ${i + 1}: start date must be before maturity (${t.maturity_date})`);
      }
    });
    return problems;
  };

  const requestBody = (action) => ({
    ...noteTermsPayload(effectiveTerms, refData),
    ...(isEdit ? { note_id: editNote.id } : {}),
    legs: legs.map((l) => ({
      leg_id: isEdit ? l.leg_id : null,
      investor_id: l.investor_id,
      nominal_amount: Number(l.nominal_amount),
      issue_date: l.own_date ? l.issue_date : null,
      commission_spread: l.commission_spread === null || l.commission_spread === '' ? null : Number(l.commission_spread),
    })),
    ...(action ? { action } : {}),
  });

  const runPreview = async () => {
    const res = await apiPost('/notes/wizard/preview', requestBody());
    setPreview(extractApiData(res, null));
  };

  /** Validate the current step; on success advance. Steps that need the server call it here. */
  const goNext = async () => {
    setError(null);
    setBusy(true);
    try {
      if (activeStep === 0) {
        if (!noteDetailsValid(terms)) {
          setError('Complete the reference (letters and digits, up to 20), start date, maturity date (after the start date), currency and investment type');
          return;
        }
        const q = new URLSearchParams({
          reference_prefix: String(terms.reference_prefix).toUpperCase(),
          maturity_date: terms.maturity_date,
          ...(terms.reference_edited ? { user_reference: effectiveUserReference(terms) } : {}),
          ...(terms.issuer_id ? { issuer_id: String(terms.issuer_id) } : {}),
          ...(isEdit ? { exclude_id: String(editNote.id) } : {}),
        });
        const found = extractApiData(await apiGet(`/notes/lookup?${q}`), {});
        const joinDraft = !isEdit && found.existing_note ? found.existing_note : null;
        const problems = legProblems(joinDraft ? termsFromNote(joinDraft, terms.reference_prefix) : terms, joinDraft);
        if (problems.length) { setError(problems.join(' · ')); return; }
        setExisting(joinDraft);
        setLookup({ reference: found.user_reference, suffixed: found.suffixed });
      }
      if (activeStep === 1) {
        if (!noteInterestValid(effectiveTerms, refData)) {
          setError('Enter the gross rate, calculation profile and interest payment frequency'); return;
        }
        await runPreview(); // Commission & Tax (step 4) and Review (step 5) both read it
      }
      if (activeStep === COMMISSION_STEP) {
        await runPreview(); // re-price after any commission edits
      }
      setActiveStep((s) => Math.min(s + 1, LAST));
    } catch (err) {
      setError(extractApiError(err, 'Could not continue'));
    } finally {
      setBusy(false);
    }
  };

  // T3-161: "Submit" saves the draft first, then opens the submit dialog (see submitNoteId).
  const save = async (action) => {
    setError(null);
    setBusy(true);
    try {
      const res = isEdit
        ? await apiPut(`/notes/${editNote.id}/wizard`, requestBody())
        : await apiPost('/notes/wizard', requestBody());
      if (action === 'submit') {
        setSubmitNoteId(res.data.id);
        return;
      }
      notify.success(res.message || 'Note saved');
      navigate(`/notes/${res.data.id}`);
    } catch (err) {
      setError(extractApiError(err, 'The note could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  const setLeg = (key, patch) => {
    setPreview(null); // investors changed: the priced preview is stale
    setLegs((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const goToStep = (i) => {
    setError(null);
    if (i >= COMMISSION_STEP && !preview) return; // Commission/Review need a fresh preview
    setActiveStep(i);
  };

  const previewErrors = (preview?.legs || []).filter((l) => l.error);
  const priced = (preview?.legs || []).filter((l) => !l.error);
  const scheduleForTable = preview ? {
    legs: priced.map((l) => ({ leg_id: l.index + 1, code: l.code, investor_name: l.investor_name, status: 'New', rows: l.rows })),
    total: preview.total,
  } : null;
  const totals = priced.reduce((a, l) => ({
    nominal: a.nominal + Number(l.nominal_amount),
    gross: a.gross + l.figures.gross_interest,
    client: a.client + l.figures.client_interest,
    commission: a.commission + l.figures.commission_amount,
    wht: a.wht + l.figures.wht_amount,
    due: a.due + l.figures.client_net_due,
    total: a.total + l.figures.total_due,
  }), { nominal: 0, gross: 0, client: 0, commission: 0, wht: 0, due: 0, total: 0 });
  // The USER reference (FIN15052026); the system reference (NTE-2026-0001) is assigned on save.
  const reference = preview?.user_reference || lookup?.reference || effectiveUserReference(terms);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>{isEdit ? `Edit Note ${editNote?.user_reference} (${editNote?.reference})` : 'New Note'}</Typography>
      {isEdit && editNote?.status === 'Rejected' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rejected{editNote.rejection_reason ? `: ${editNote.rejection_reason}` : ''}. Correct the note and submit it again.
        </Alert>
      )}
      <Paper sx={{ p: 3 }}>
        <WizardStepper steps={STEPS} activeStep={activeStep} disabled={busy}
          completedSteps={[...Array(activeStep).keys()]} onStepClick={goToStep} />

        {/* ---------------- Navigation (contract-wizard layout) ---------------- */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2, mb: 3,
          borderTop: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate(isEdit ? `/notes/${editNote.id}` : '/notes')} disabled={busy}>Cancel</Button>
          {activeStep > 0 && (
            <Button variant="outlined" startIcon={<BackIcon />} disabled={busy}
              onClick={() => { setError(null); setActiveStep((s) => s - 1); }}>
              Back
            </Button>
          )}
          {activeStep < LAST && (
            <Button variant="contained" endIcon={busy ? <CircularProgress size={18} color="inherit" /> : <NextIcon />}
              onClick={goNext} disabled={busy}>
              Next
            </Button>
          )}
          {activeStep === LAST && (
            <>
              <Button variant="outlined" startIcon={busy ? <CircularProgress size={18} /> : <SaveIcon />}
                onClick={() => save('draft')} disabled={busy}>
                Save as Draft
              </Button>
              <Tooltip title={!canSubmit ? 'Your role cannot submit notes'
                : previewErrors.length ? 'Fix the investors that cannot be priced first' : ''}>
                <span>
                  <Button variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SubmitIcon />}
                    onClick={() => save('submit')} disabled={busy || !canSubmit || previewErrors.length > 0}>
                    {settings?.requireApproval === false ? 'Submit & Activate' : 'Submit for Approval'}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ---------------- Step 1: Note Details (+ investors) ---------------- */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Every investor on a note shares its start and maturity dates, gross rate and calculation profile; each has
              their own amount and commission. An investor who joined on a different day can have their own start date.
            </Typography>
            <NoteDetailsFields form={terms} setForm={(fn) => { setPreview(null); setTerms(fn); }} refData={refData}
              systemReference={editNote?.reference} />

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Investors</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>Investor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start date</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {legs.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell>
                      {/* The contract wizard's investor picker: search, plus "+ Create New Investor"
                          (Stakeholder write permission). An investor already chosen on another
                          row is not offered again — one investor per note (T3-159). */}
                      <StakeholderAutocomplete stakeholderType="investor"
                        options={investors.filter((i) => i.id === l.investor_id
                          || !legs.some((o) => o.key !== l.key && o.investor_id === i.id))}
                        value={l.investor_id}
                        onChange={(id) => setLeg(l.key, { investor_id: id || null })}
                        onStakeholderCreated={(created) => {
                          setInvestors((list) => [...list, created].sort((a, b) => String(a.name).localeCompare(String(b.name))));
                          setLeg(l.key, { investor_id: created.id });
                        }}
                        countries={refData.countries} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={l.nominal_amount} fullWidth
                        onChange={(e) => setLeg(l.key, { nominal_amount: e.target.value })}
                        slotProps={{ htmlInput: { min: 0, step: '0.01' } }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel sx={{ mr: 0, whiteSpace: 'nowrap' }}
                          control={<Checkbox size="small" checked={l.own_date}
                            onChange={(e) => setLeg(l.key, { own_date: e.target.checked, issue_date: e.target.checked ? (l.issue_date || terms.issue_date) : '' })} />}
                          label={<Typography variant="body2">Own start date</Typography>} />
                        {l.own_date ? (
                          <TextField size="small" type="date" value={l.issue_date}
                            onChange={(e) => setLeg(l.key, { issue_date: e.target.value })}
                            slotProps={{ htmlInput: { max: terms.maturity_date } }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {terms.issue_date ? formatShortDate(terms.issue_date) : 'Note start date'}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" disabled={legs.length === 1}
                        onClick={() => { setPreview(null); setLegs((ls) => ls.filter((x) => x.key !== l.key)); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => { setPreview(null); setLegs((ls) => [...ls, emptyLeg()]); }}>
              Add investor
            </Button>
          </Box>
        )}

        {/* ---------------- Step 2: Interest & Payments ---------------- */}
        {activeStep === 1 && (
          <Box>
            {existing ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Draft note <strong>{existing.user_reference}</strong> ({existing.reference}) already has this reference ({existing.legs.length} investor(s)).
                These investors will be <strong>added to it on its terms</strong>, shown below read-only.
              </Alert>
            ) : (
              <Alert severity={lookup?.suffixed ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {lookup?.suffixed
                  ? <>A submitted note already uses this reference, so this note will be <strong>{lookup.reference}</strong>.</>
                  : <>{isEdit ? 'Note' : 'New note'} <strong>{lookup?.reference || effectiveUserReference(terms)}</strong></>}
                {' '}· starts {formatShortDate(terms.issue_date)} · matures {formatShortDate(terms.maturity_date)}
              </Alert>
            )}
            <NoteInterestFields form={effectiveTerms} setForm={(fn) => { setPreview(null); setTerms(fn); }}
              refData={refData} disabled={Boolean(existing)} />
          </Box>
        )}

        {/* ---------------- Step 3: Adhoc Events ---------------- */}
        {activeStep === 2 && (
          <Alert severity="info">
            Adhoc capital and interest events are not yet available for notes. Each investor&apos;s schedule runs from their
            start date to maturity on the note&apos;s profile and payment frequency. Continue to set each investor&apos;s commission.
          </Alert>
        )}

        {/* ---------------- Step 4: Commission & Tax (editable commission) ---------------- */}
        {activeStep === COMMISSION_STEP && preview && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each investor&apos;s commission spread is pre-filled from their commission override (Stakeholders), otherwise the
              staff{settings ? ` (${settings.spreadStaff}%)` : ''} or standard{settings ? ` (${settings.spreadStandard}%)` : ''} spread.
              Change it here for this note; net rate = gross rate {pct(preview.terms.gross_rate)} less the spread. Figures update
              when you continue. WHT is on gross interest{settings?.companyWhtExempt ? '; companies are exempt' : ''}.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Code', 'Investor', 'Amount', 'Commission spread', 'Net Rate', 'Commission', 'WHT', 'Staff?', 'Company?']
                    .map((h) => <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                      align={['Code', 'Investor'].includes(h) ? 'left' : 'right'}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.legs.map((pl) => {
                  const leg = legs[pl.index];
                  const edited = isSpreadEdited(leg);
                  const setSpread = (v) => setLegs((ls) => ls.map((x) => (x.key === leg.key ? { ...x, commission_spread: v } : x)));
                  return (
                    <TableRow key={pl.index}>
                      <TableCell>{pl.code}</TableCell>
                      <TableCell>{pl.investor_name}</TableCell>
                      <TableCell align="right">{money(pl.nominal_amount)}</TableCell>
                      {pl.error ? (
                        <TableCell colSpan={6}><Typography variant="body2" color="error">{pl.error}</Typography></TableCell>
                      ) : (
                        <>
                          <TableCell align="right" sx={{ minWidth: 220 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              {edited ? (
                                <Tooltip title={`Reset to the default (${pct(pl.pricing.default_spread)}, ${SPREAD_SOURCES[pl.pricing.default_spread_source]})`}>
                                  <IconButton size="small" onClick={() => setSpread(null)}>
                                    <ResetIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Chip size="small" variant="outlined" label={SPREAD_SOURCES[pl.pricing.default_spread_source] || 'Default'} />
                              )}
                              <TextField size="small" sx={{ width: 100 }}
                                value={edited ? leg.commission_spread : String(pl.pricing.default_spread ?? '')}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => { if (SPREAD_TEXT.test(e.target.value)) setSpread(e.target.value); }}
                                onBlur={() => { if (edited && leg.commission_spread === '') setSpread(null); }}
                                slotProps={{
                                  htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } },
                                  input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                                }} />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{pct(pl.pricing.client_rate)}</TableCell>
                          <TableCell align="right">{money(pl.figures.commission_amount)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={WHT_SOURCES[pl.pricing.wht_source] || pl.pricing.wht_source || ''}>
                              <span>{pct(pl.pricing.wht_rate)}{pl.pricing.wht_gross_up ? ' (gross-up)' : ''}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{pl.pricing.is_staff ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">{pl.pricing.is_company ? 'Yes' : 'No'}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
                <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell align="right">{money(totals.nominal)}</TableCell>
                  <TableCell colSpan={2} />
                  <TableCell align="right">{money(totals.commission)}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              The commission column reflects the spreads as last priced; press Next to re-price after editing.
            </Typography>
          </Box>
        )}

        {/* ---------------- Step 5: Review & Schedule ---------------- */}
        {activeStep === LAST && preview && (
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {reference}{preview.existing_note ? ' (adding to the existing draft note)' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Gross {pct(preview.terms.gross_rate)} · starts {formatShortDate(preview.terms.issue_date)} · matures{' '}
              {formatShortDate(preview.terms.maturity_date)} · {preview.terms.frequency_name} · calculated on{' '}
              <strong>{preview.terms.profile?.name}</strong>
            </Typography>
            {previewErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {previewErrors.map((l) => `${l.code} ${l.investor_name || ''}: ${l.error}`).join(' · ')}
              </Alert>
            )}
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  {['Code', 'Client', 'Amount', 'Spread', 'Net Rate', 'Start', 'Days', 'Gross Interest',
                    'Net Interest', 'Commission', 'WHT', 'Net Interest Due', 'Total Due'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                      align={['Code', 'Client', 'Start'].includes(h) ? 'left' : 'right'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.legs.map((l) => (
                  <TableRow key={l.index}>
                    <TableCell>{l.code}</TableCell>
                    <TableCell>{l.investor_name}</TableCell>
                    {l.error ? (
                      <TableCell colSpan={11}><Typography variant="body2" color="error">{l.error}</Typography></TableCell>
                    ) : (
                      <>
                        <TableCell align="right">{money(l.nominal_amount)}</TableCell>
                        <TableCell align="right">
                          {pct(l.pricing.commission_spread)}
                          {l.pricing.spread_source === 'manual' && <Chip size="small" label="edited" variant="outlined" sx={{ ml: 0.5, height: 18 }} />}
                        </TableCell>
                        <TableCell align="right">{pct(l.pricing.client_rate)}</TableCell>
                        <TableCell>{formatShortDate(l.issue_date)}</TableCell>
                        <TableCell align="right">{l.figures.days}</TableCell>
                        <TableCell align="right">{money(l.figures.gross_interest)}</TableCell>
                        <TableCell align="right">{money(l.figures.client_interest)}</TableCell>
                        <TableCell align="right">{money(l.figures.commission_amount)}</TableCell>
                        <TableCell align="right">{l.figures.wht_amount === 0 ? '-' : money(l.figures.wht_amount)}</TableCell>
                        <TableCell align="right">{money(l.figures.client_net_due)}</TableCell>
                        <TableCell align="right">{money(l.figures.total_due)}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell align="right">{money(totals.nominal)}</TableCell>
                  <TableCell colSpan={4} />
                  <TableCell align="right">{money(totals.gross)}</TableCell>
                  <TableCell align="right">{money(totals.client)}</TableCell>
                  <TableCell align="right">{money(totals.commission)}</TableCell>
                  <TableCell align="right">{money(totals.wht)}</TableCell>
                  <TableCell align="right">{money(totals.due)}</TableCell>
                  <TableCell align="right">{money(totals.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Typography variant="h6" sx={{ mb: 1 }}>Amortization schedule</Typography>
            <NoteScheduleTable schedule={scheduleForTable} />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Calculation profile</Typography>
              <ProfileSummary profile={preview.terms.profile} />
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              {settings?.requireApproval === false
                ? 'Submitting fixes every investor’s pricing, stores their schedules and makes the note Active immediately.'
                : 'Submitting sends the whole note — every investor together — for approval by another user, fixing each investor’s pricing and storing their schedules. Save as Draft keeps everything editable.'}
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Saved as a draft; now collect any required documents and submit (T3-161). Closing the
          dialog leaves the saved draft on its note page. */}
      <SubmitForApprovalDialog open={Boolean(submitNoteId)} noteId={submitNoteId}
        onClose={() => { const id = submitNoteId; setSubmitNoteId(null); navigate(`/notes/${id}`); }}
        onSubmitted={() => { const id = submitNoteId; setSubmitNoteId(null); notify.success('Note submitted'); navigate(`/notes/${id}`); }} />
    </Box>
  );
};

export default NoteCreate;
